import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { db, auth } from "../../firebase/config";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  limit,
  query,
  where,
  onSnapshot,
  getDocs,
  addDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import ReportButton from "../Common/ReportButton";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import {
  PRODUCT_AVAILABILITY,
  getProductAvailabilityLabel,
  getProductAvailabilityStatus,
  getFarmStandAvailabilityLabel,
  getServiceAvailabilityLabel,
} from "../../constants/availability";
import { MARKETPLACE_SECTIONS } from "../../constants/plantsGarden";
import { formatAvailability, formatProductPrice } from "../../utils/productDisplay";
import InstallAppButton from "../Common/InstallAppButton";
import {
  getBroadStorefrontLocation,
  getGeneratedStorefrontSlug,
  getProfilePath,
  getPublicGalleryImages,
  getPublicStorefrontBadges,
  getPublicStorefrontName,
  getStoredStorefrontSlug,
  getStorefrontReadiness,
  getStorefrontUrl,
  isActivePublicStorefrontProduct,
  isStorefrontEnabled,
  normalizeStorefrontSlug,
  STOREFRONT_SLUG_FIELDS,
} from "../../utils/storefront";
import {
  canShowStandDirections,
  getStandDirectionsUrl,
  getStandPublicLocationLabel,
} from "../../utils/standLocation";
import FarmAvatar from "../Common/FarmAvatar";
import { getProfileRoleLabel } from "../../utils/profileRoles";
import "./ProfileStorefront.css";

const getUserRoles = (user) => {
  if (Array.isArray(user?.roles) && user.roles.length > 0) return user.roles;
  if (user?.type) return [user.type];
  return ["community"];
};

const formatRole = (role) => {
  const map = {
    landowner: "🌾 Land Owner",
    farmer: "🚜 Farmer",
    homesteader: "🏡 Homesteader",
    day_worker: "🛠 Day Worker",
    community: "🤝 Community Member",
    operator: "🛠 Operator",
    event_planner: "🎪 Event Planner",
  };

  return map[role] || getProfileRoleLabel(role);
};

const formatDeal = (deal) => {
  const map = {
    per_acre: "Per Acre",
    per_bale: "Per Bale",
    share: "50/50 Share",
    flat: "Flat Pay",
    quote: "Quote",
  };

  return map[deal] || deal || "Not listed";
};

const formatDate = (timestamp) => {
  if (timestamp?.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }

  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString();
  }

  return "Recently";
};

const formatMemberSince = (timestamp) => {
  if (timestamp?.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  }

  return "Recently";
};

const getFieldJobLocation = (job) => {
  const firstField = Array.isArray(job?.fields) ? job.fields[0] : null;

  const city = job?.city || firstField?.city || "";
  const state = job?.state || firstField?.state || "";

  const joined = [city, state].filter(Boolean).join(", ");

  return joined || job?.locationText || "Location not listed";
};

const getFieldTotalAcres = (job) => {
  if (job?.totalAcres) return Number(job.totalAcres).toFixed(2);

  if (Array.isArray(job?.fields)) {
    const total = job.fields.reduce(
      (sum, field) => sum + (Number(field.acres) || 0),
      0
    );

    return total.toFixed(2);
  }

  return "0.00";
};

const getFieldCrop = (job) => {
  const firstField = Array.isArray(job?.fields) ? job.fields[0] : null;
  return job?.grassType || firstField?.category || "Field work";
};

const getDayJobLocation = (job) => {
  const joined = [job?.city, job?.state].filter(Boolean).join(", ");
  return job?.locationText || joined || "Location not listed";
};

const isActiveProfileRecord = (profile = {}) => {
  const status = String(profile.accountStatus || "active").toLowerCase();
  return (
    profile &&
    status !== "deactivated" &&
    status !== "deleted" &&
    profile.deleted !== true &&
    profile.isDeleted !== true
  );
};

const isPublicProfileRecord = (profile = {}) =>
  isActiveProfileRecord(profile) && profile.isPublic !== false;

const canViewProfileRecord = (profile = {}, viewerUid = "", viewerProfile = {}) =>
  isPublicProfileRecord(profile) ||
  Boolean(viewerUid && profile.id === viewerUid) ||
  viewerProfile?.admin === true ||
  viewerProfile?.isAdmin === true;

const getProfileSlugCandidates = (profile = {}) =>
  [
    getStoredStorefrontSlug(profile),
    getGeneratedStorefrontSlug(profile),
  ].filter(Boolean);

const dedupeProfilesById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const findProfileBySlug = async (slug) => {
  const cleanSlug = normalizeStorefrontSlug(slug);
  if (!cleanSlug) return { profile: null, ambiguous: false };

  const exactMatches = [];

  for (const field of STOREFRONT_SLUG_FIELDS) {
    const snapshot = await getDocs(
      query(collection(db, "users"), where(field, "==", cleanSlug), limit(3))
    );
    snapshot.forEach((docSnap) => {
      exactMatches.push({ id: docSnap.id, ...docSnap.data() });
    });
  }

  const uniqueExactMatches = dedupeProfilesById(exactMatches).filter(
    (profile) => isPublicProfileRecord(profile) && isStorefrontEnabled(profile)
  );
  if (uniqueExactMatches.length > 1) return { profile: null, ambiguous: true };
  if (uniqueExactMatches.length === 1) return { profile: uniqueExactMatches[0], ambiguous: false };

  const usersSnapshot = await getDocs(collection(db, "users"));
  const generatedMatches = [];
  usersSnapshot.forEach((docSnap) => {
    const profile = { id: docSnap.id, ...docSnap.data() };
    if (
      isPublicProfileRecord(profile) &&
      isStorefrontEnabled(profile) &&
      getProfileSlugCandidates(profile).includes(cleanSlug)
    ) {
      generatedMatches.push(profile);
    }
  });

  const uniqueGeneratedMatches = dedupeProfilesById(generatedMatches);
  if (uniqueGeneratedMatches.length !== 1) {
    return { profile: null, ambiguous: uniqueGeneratedMatches.length > 1 };
  }

  return { profile: uniqueGeneratedMatches[0], ambiguous: false };
};



const Profile = ({ userProfile: currentUserProfile }) => {
  const { userId: profileParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const reviewsRef = useRef(null);

  const [resolvedUserId, setResolvedUserId] = useState(profileParam || "");
  const [user, setUser] = useState(null);
  const [profileRouteLoading, setProfileRouteLoading] = useState(true);
  const [profileUnavailable, setProfileUnavailable] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [storefrontCopyStatus, setStorefrontCopyStatus] = useState("");
  const [completedJobs, setCompletedJobs] = useState([]);
  const [completedDayJobs, setCompletedDayJobs] = useState([]);
  const [farmStands, setFarmStands] = useState([]);
  const [productOrders, setProductOrders] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
const [myProductReviews, setMyProductReviews] = useState([]);
  const [productReviewTarget, setProductReviewTarget] = useState(null);
const [productReviewRating, setProductReviewRating] = useState(5);
const [productReviewComment, setProductReviewComment] = useState("");
const [submittingProductReview, setSubmittingProductReview] = useState(false);
 const [buyerProductOrders, setBuyerProductOrders] = useState([]); 
  const userId = resolvedUserId || profileParam;
  const isOwnProfile = auth.currentUser?.uid === userId;

  useEffect(() => {
    let isMounted = true;

    const resolveProfileRoute = async () => {
      const cleanParam = String(profileParam || "").trim();
      setProfileRouteLoading(true);
      setProfileUnavailable(false);

      if (!cleanParam) {
        setResolvedUserId("");
        setUser(null);
        setProfileUnavailable(true);
        setProfileRouteLoading(false);
        return;
      }

      try {
        const directSnap = await getDoc(doc(db, "users", cleanParam));
        if (!isMounted) return;

        if (directSnap.exists()) {
          const profile = { id: directSnap.id, ...directSnap.data() };

          if (!canViewProfileRecord(profile, auth.currentUser?.uid, currentUserProfile || {})) {
            setResolvedUserId(cleanParam);
            setUser(null);
            setProfileUnavailable(true);
            setProfileRouteLoading(false);
            return;
          }

          const profileSlug = getStoredStorefrontSlug(profile);
          if (
            profileSlug &&
            profileSlug !== cleanParam &&
            isStorefrontEnabled(profile)
          ) {
            navigate(getProfilePath(profile), {
              replace: true,
              state: location.state,
            });
          }

          setResolvedUserId(directSnap.id);
          setUser(profile);
          setProfileUnavailable(false);
          setProfileRouteLoading(false);
          return;
        }

        const result = await findProfileBySlug(cleanParam);
        if (!isMounted) return;

        if (!result.profile || result.ambiguous) {
          setResolvedUserId(cleanParam);
          setUser(null);
          setProfileUnavailable(true);
          setProfileRouteLoading(false);
          return;
        }

        setResolvedUserId(result.profile.id);
        setUser(result.profile);
        setProfileUnavailable(false);
        setProfileRouteLoading(false);
      } catch (error) {
        console.error("Error resolving profile route:", error);
        if (isMounted) {
          setResolvedUserId(cleanParam);
          setUser(null);
          setProfileUnavailable(true);
          setProfileRouteLoading(false);
        }
      }
    };

    resolveProfileRoute();

    return () => {
      isMounted = false;
    };
  }, [profileParam, currentUserProfile, navigate, location.state]);

  useEffect(() => {
    if (!location.state?.scrollToReviews) return;

    const timer = setTimeout(() => {
      reviewsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [location.state?.scrollToReviews, reviews.length]);

  useEffect(() => {
    const q = query(
      collection(db, "reviews"),
      where("revieweeId", "==", userId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const revs = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setReviews(revs);
    }
  );

    return () => unsub();
  }, [userId]);

  useEffect(() => {
  if (!isOwnProfile || !auth.currentUser?.uid) {
    setBuyerProductOrders([]);
    return;
  }

  const q = query(
    collection(db, "productOrders"),
    where("buyerId", "==", auth.currentUser.uid),
    where("status", "==", "completed")
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const orders = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .sort((a, b) => {
        const aTime = a.completedAt?.seconds || a.createdAt?.seconds || 0;
        const bTime = b.completedAt?.seconds || b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

    setBuyerProductOrders(orders);
  });

  return () => unsub();
}, [isOwnProfile]);

  useEffect(() => {
  const q = query(
    collection(db, "productReviews"),
    where("sellerId", "==", userId)
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const list = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

    setProductReviews(list);
  });

  return () => unsub();
}, [userId]);

  useEffect(() => {
    const q = query(
      collection(db, "fields"),
      where("status", "==", "completed"),
      where("completedBy", "==", userId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const jobs = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        jobKind: "field",
        ...docSnap.data(),
      }));

      setCompletedJobs(jobs);
    });

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    const q = query(
      collection(db, "dayJobs"),
      where("status", "==", "completed"),
      where("completedBy", "==", userId)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const jobs = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        jobKind: "day",
        ...docSnap.data(),
      }));

      setCompletedDayJobs(jobs);
    });

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    const q = query(collection(db, "products"), where("userId", "==", userId));

    const unsub = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) 
      .filter((product) => product.isArchived !== true);;

      

      setProducts(items);
    });

    return () => unsub();
  }, [userId]);

  useEffect(() => {
  const q = query(
    collection(db, "farmStands"),
    where("ownerId", "==", userId)
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const stands = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((stand) => stand.isArchived !== true && stand.status !== "archived");

    setFarmStands(stands);
  });

  return () => unsub();
}, [userId]);

useEffect(() => {
  if (!isOwnProfile || !auth.currentUser?.uid) {
    setProductOrders([]);
    return;
  }

  const q = query(
    collection(db, "productOrders"),
    where("sellerId", "==", auth.currentUser.uid)
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const orders = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .filter((order) => order.status !== "archived")
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

    setProductOrders(orders);
  });

  return () => unsub();
}, [isOwnProfile]);

useEffect(() => {
  if (!isOwnProfile || !auth.currentUser?.uid) {
    setMyProductReviews([]);
    return;
  }

  const q = query(
    collection(db, "productReviews"),
    where("buyerId", "==", auth.currentUser.uid)
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    setMyProductReviews(list);
  });

  return () => unsub();
}, [isOwnProfile]);

const reviewsByJobId = useMemo(() => {
  return reviews.reduce((acc, review) => {
    const key = review.jobId;
    if (!key) return acc;

    if (!acc[key]) acc[key] = [];
    acc[key].push(review);

    return acc;
  }, {});
}, [reviews]);

const getJobReviews = (job) => {
  return reviewsByJobId[job.id] || [];
};

  const allCompletedWork = useMemo(() => {
    return [...completedJobs, ...completedDayJobs].sort((a, b) => {
      const aTime = a.completedAt?.seconds || 0;
      const bTime = b.completedAt?.seconds || 0;
      return bTime - aTime;
    });
  }, [completedJobs, completedDayJobs]);

  const myProductReviewsByOrderId = useMemo(() => {
  return myProductReviews.reduce((acc, review) => {
    if (!review.productOrderId) return acc;
    acc[review.productOrderId] = review;
    return acc;
  }, {});
}, [myProductReviews]);

const hasReviewedProductOrder = (order) => {
  return Boolean(order.buyerReviewed || myProductReviewsByOrderId[order.id]);
};

const completedBuyerOrdersNeedingReview = buyerProductOrders.filter(
  (order) => !hasReviewedProductOrder(order)
);

const getReviewsForProduct = (productId) => {
  return productReviews.filter((review) => review.productId === productId);
};

  const handleMessage = async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) return alert("Login required");
    if (currentUser.uid === userId) return;

    const q = query(
      collection(db, "conversations"),
      where("users", "array-contains", currentUser.uid)
    );

    const snapshot = await getDocs(q);

    let existingConversationId = null;

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();

      if (Array.isArray(data.users) && data.users.includes(userId)) {
        existingConversationId = docSnap.id;
      }
    });

    if (existingConversationId) {
      navigate(`/chat/${existingConversationId}`);
      return;
    }

    const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
    const otherUserDoc = await getDoc(doc(db, "users", userId));

    const currentUserData = currentUserDoc.data() || {};
    const otherUserData = otherUserDoc.data() || {};

    const newConversation = await addDoc(collection(db, "conversations"), {
      users: [currentUser.uid, userId],
      participantsInfo: {
        [currentUser.uid]: {
          name: currentUserData?.name || currentUser.email,
          email: currentUser.email,
          photo: currentUserData?.profilePicture || currentUserData?.photo || "",
          type: currentUserData?.type || "farmer",
        },
        [userId]: {
          name: otherUserData?.name || otherUserData?.email || "User",
          email: otherUserData?.email || "",
          photo: otherUserData?.profilePicture || otherUserData?.photo || "",
          type: otherUserData?.type || "operator",
        },
      },
      createdAt: serverTimestamp(),
      lastMessage: "",
      lastMessageAt: serverTimestamp(),
      lastSenderId: "",
      unreadCount: {
        [currentUser.uid]: 0,
        [userId]: 0,
      },
    });

    navigate(`/chat/${newConversation.id}`);
  };

  const confirmProductSale = async (order) => {
  const currentUser = auth.currentUser;

  if (!currentUser?.uid) return;

  if (order.sellerId !== currentUser.uid) {
    alert("Only the seller can confirm this sale.");
    return;
  }

  const confirmed = window.confirm(
    `Confirm sale of ${order.quantity} ${order.productUnit || "item"}${
      Number(order.quantity || 1) === 1 ? "" : "s"
    }? This will update your product stock.`
  );

  if (!confirmed) return;

  try {
    let completedOrderData = null;
    let completedProductData = null;
    let soldQtyForFollowUp = Number(order.quantity || 1);

    await runTransaction(db, async (transaction) => {
      const orderRef = doc(db, "productOrders", order.id);
      const productRef = doc(db, "products", order.productId);

      const orderSnap = await transaction.get(orderRef);
      const productSnap = await transaction.get(productRef);

      if (!orderSnap.exists()) {
        throw new Error("Order not found.");
      }

      if (!productSnap.exists()) {
        throw new Error("Product not found.");
      }

      const latestOrder = orderSnap.data();
      const latestProduct = productSnap.data();
      completedOrderData = latestOrder;
      completedProductData = latestProduct;

      if (latestOrder.status === "completed") {
        throw new Error("This sale has already been completed.");
      }

      if (latestOrder.status === "declined") {
        throw new Error("This sale was already declined.");
      }

      const currentQty =
        typeof latestProduct.quantityAvailable === "number"
          ? latestProduct.quantityAvailable
          : typeof latestProduct.quantity === "number"
          ? latestProduct.quantity
          : 0;

      const soldQty = Number(latestOrder.quantity || order.quantity || 1);
      soldQtyForFollowUp = soldQty;

      if (soldQty > currentQty) {
        throw new Error(`Only ${currentQty} available.`);
      }

      const newQty = Math.max(currentQty - soldQty, 0);

      transaction.update(productRef, {
        quantityAvailable: newQty,
        status: newQty <= 0 ? "out_of_stock" : "active",
        availabilityStatus:
          newQty <= 0
            ? PRODUCT_AVAILABILITY.OUT_OF_STOCK
            : PRODUCT_AVAILABILITY.AVAILABLE_NOW,
        updatedAt: serverTimestamp(),
      });

      transaction.update(orderRef, {
        status: "completed",
        completedAt: serverTimestamp(),
        confirmedBy: currentUser.uid,
      });
    });

    alert("Sale confirmed and stock updated ✅");

    if (completedProductData?.linkedInventoryId) {
      const reduceInventory = window.confirm(
        `Reduce inventory by ${soldQtyForFollowUp} ${completedProductData.unit || order.productUnit || "units"}?`
      );

      if (reduceInventory) {
        const inventoryRef = doc(db, "farmInventory", completedProductData.linkedInventoryId);
        const inventorySnap = await getDoc(inventoryRef);

        if (inventorySnap.exists() && inventorySnap.data().ownerId === currentUser.uid) {
          const inventory = inventorySnap.data();
          await updateDoc(inventoryRef, {
            quantity: Math.max(Number(inventory.quantity || 0) - soldQtyForFollowUp, 0),
            updatedAt: serverTimestamp(),
          });
        }
      }
    }

    if (window.confirm("Add this completed product sale to Money Log?")) {
      await addDoc(collection(db, "farmMoneyLogs"), {
        ownerId: currentUser.uid,
        type: "income",
        title: `Sale: ${completedOrderData?.productName || order.productName || "Farm product"}`,
        category: "product_sales",
        amount:
          Number(completedOrderData?.totalPrice || order.totalPrice || 0) ||
          Number(completedOrderData?.price || order.price || completedProductData?.price || 0) * soldQtyForFollowUp,
        date: new Date().toISOString().slice(0, 10),
        linkedRecordType: "product_order",
        linkedRecordId: order.id,
        notes: `Product ID: ${order.productId || ""}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Confirm sale failed:", error);
    alert(error.message?.includes("available") ? error.message : "Could not confirm sale. Please try again.");
  }
};

const declineProductSale = async (order) => {
  const currentUser = auth.currentUser;

  if (!currentUser?.uid) return;

  if (order.sellerId !== currentUser.uid) {
    alert("Only the seller can decline this request.");
    return;
  }

  const confirmed = window.confirm(
    `Decline request for ${order.quantity} ${order.productUnit || "item"}${
      Number(order.quantity || 1) === 1 ? "" : "s"
    } of ${order.productName || "this product"}?`
  );

  if (!confirmed) return;

  try {
    await updateDoc(doc(db, "productOrders", order.id), {
      status: "declined",
      declinedAt: serverTimestamp(),
      declinedBy: currentUser.uid,
    });

    alert("Request declined.");
  } catch (error) {
    console.error("Decline sale failed:", error);
    alert("Could not decline request.");
  }
};

const submitProductReview = async () => {
  const currentUser = auth.currentUser;

  if (!currentUser?.uid || !productReviewTarget) return;
  if (!requireVerifiedEmail(currentUser, currentUserProfile)) return;

  const order = productReviewTarget;

  if (order.buyerId !== currentUser.uid) {
    alert("Only the buyer can review this product sale.");
    return;
  }

  if ((order.status || "") !== "completed") {
    alert("You can review after the sale is completed.");
    return;
  }

  if (!productReviewComment.trim()) {
    alert("Please add a short review.");
    return;
  }

  if (hasReviewedProductOrder(order)) {
    alert("You already reviewed this purchase.");
    setProductReviewTarget(null);
    return;
  }

  try {
    setSubmittingProductReview(true);

    await addDoc(collection(db, "productReviews"), {
      productOrderId: order.id,
      productId: order.productId,
      productName: order.productName || "Farm Product",
      productImage: order.productImage || "",

      sellerId: order.sellerId,
      sellerName: order.sellerName || "Seller",

      buyerId: currentUser.uid,
      buyerName: order.buyerName || currentUser.email || "Buyer",
      buyerPhoto: order.buyerPhoto || "",

      rating: Number(productReviewRating || 5),
      comment: productReviewComment.trim(),

      quantity: Number(order.quantity || 1),
      productUnit: order.productUnit || "item",

      createdAt: serverTimestamp(),
    });

    await updateDoc(doc(db, "productOrders", order.id), {
      buyerReviewed: true,
      buyerReviewedAt: serverTimestamp(),
    });

    alert("Product review added ✅");

    setProductReviewTarget(null);
    setProductReviewRating(5);
    setProductReviewComment("");
  } catch (error) {
    console.error("Product review failed:", error);
    alert("Could not submit product review. Please try again.");
  } finally {
    setSubmittingProductReview(false);
  }
};

const handleArchiveFarmStand = async (standId) => {
  const confirmed = window.confirm(
    "Remove this stand / pickup spot from your profile?"
  );

  if (!confirmed) return;

  try {
    await updateDoc(doc(db, "farmStands", standId), {
      isArchived: true,
      status: "archived",
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error archiving stand / pickup spot:", error);
    alert("Could not remove stand / pickup spot. Please try again.");
  }
};

  const handleArchiveProduct = async (productId) => {
  const confirmed = window.confirm(
    "Remove this product from the market and your profile list?"
  );

  if (!confirmed) return;

  try {
    await updateDoc(doc(db, "products", productId), {
      isArchived: true,
      archivedAt: serverTimestamp(),
      status: "archived",
      availabilityStatus: PRODUCT_AVAILABILITY.ARCHIVED,
    });
  } catch (error) {
    console.error("Error archiving product:", error);
    alert("Could not remove product. Please try again.");
  }
};

const viewProductListing = (product) => {
  const marketplaceSection =
    product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL;

  if (marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN) {
    navigate("/plants-garden", {
      state: {
        focusProductId: product.id,
      },
    });
    return;
  }

  navigate("/home", {
    state: {
      dashboardView: "market",
      focusProductId: product.id,
    },
  });
};

const currentUserOwnsProduct = (product) => {
  const currentUserId = auth.currentUser?.uid;
  if (!currentUserId || !isOwnProfile) return false;

  return [product?.userId, product?.ownerId, product?.sellerId]
    .filter(Boolean)
    .includes(currentUserId);
};

const editProductListing = (product) => {
  if (!product?.id || !currentUserOwnsProduct(product)) return;

  const marketplaceSection =
    product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL;
  const editRoute =
    marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN
      ? "/plants-garden/new"
      : "/add-product";

  navigate(`${editRoute}?edit=${encodeURIComponent(product.id)}`);
};

  const featuredImage = useMemo(() => {
    if (!user?.workImages?.length) return null;
    return user.workImages.find((img) => img.featured) || user.workImages[0];
  }, [user]);
  const publicGalleryImages = useMemo(
    () => getPublicGalleryImages(user || {}),
    [user]
  );
  const profileStorefrontEnabled = useMemo(
    () => isStorefrontEnabled(user || {}, { products, farmStands }),
    [farmStands, products, user]
  );
  const canShowStorefrontContent = isOwnProfile || profileStorefrontEnabled;
  const publicStorefrontProducts = useMemo(
    () => products.filter(isActivePublicStorefrontProduct),
    [products]
  );
  const storefrontReadiness = useMemo(
    () =>
      getStorefrontReadiness({
        profile: user || {},
        products: publicStorefrontProducts,
        galleryImages: publicGalleryImages,
      }),
    [publicGalleryImages, publicStorefrontProducts, user]
  );
  const publicStorefrontUrl = useMemo(
    () => getStorefrontUrl(user || {}, "", { products, farmStands }),
    [farmStands, products, user]
  );
  const hasPermanentStorefrontSlug = Boolean(getStoredStorefrontSlug(user || {}));
  const publicStorefrontName = getPublicStorefrontName(user || {});
  const publicStorefrontLocation = getBroadStorefrontLocation(user || {});
  const profileStorefrontBadges = getPublicStorefrontBadges({
    profile: user || {},
    products: publicStorefrontProducts,
    galleryImages: publicGalleryImages,
  });

  const averageRating = Number(user?.rating || 0).toFixed(1);
  const completedWorkCount = allCompletedWork.length;
  const memberSince = formatMemberSince(user?.createdAt);
const pendingProductOrders = productOrders.filter(
  (order) => (order.status || "pending") === "pending"
);

const completedProductOrders = productOrders.filter(
  (order) => order.status === "completed"
);

const declinedProductOrders = productOrders.filter(
  (order) => order.status === "declined"
);

const handleCopyStorefrontLink = async () => {
  if (!publicStorefrontUrl) return;

  try {
    await navigator.clipboard.writeText(publicStorefrontUrl);
    setStorefrontCopyStatus("Copied storefront link.");
  } catch (error) {
    console.warn("Clipboard copy failed:", error);
    window.prompt("Copy your storefront link:", publicStorefrontUrl);
    setStorefrontCopyStatus("Storefront link ready to copy.");
  }

  window.setTimeout(() => setStorefrontCopyStatus(""), 3000);
};

  if (!user && profileRouteLoading) {
    return <div className="card page-shell page-shell-narrow">Loading profile...</div>;
  }

  if (!user || profileUnavailable) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>Profile unavailable</h2>
        <p>This profile could not be found or is not public.</p>
      </div>
    );
  }

  if (
    !isOwnProfile &&
    (user.accountStatus === "deactivated" ||
      user.accountStatus === "deleted" ||
      user.isPublic === false)
  ) {
    return (
      <div className="card page-shell page-shell-narrow">
        <h2>Profile unavailable</h2>
        <p>This account is no longer active.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-hero-card">
        <div className="profile-hero">
          <div className="profile-hero-media">
            <FarmAvatar profile={user} className="hero-avatar" />
          </div>

          <div className="hero-info">
            <div className="hero-badges">
              {getUserRoles(user).map((role) => (
                <span key={role} className="profile-pill">
                  {formatRole(role)}
                </span>
              ))}

              <span className="profile-pill">
                ⭐ {averageRating} ({user.reviewCount || 0})
              </span>

              {user.founder === true && user.founderSpotActive !== false && (
                <span className="profile-pill">
                  🌾 Founder
                </span>
              )}
            </div>

            {(user.serviceAvailabilityStatus || "available") !== "available" && (
              <span
                className={`profile-pill availability-${
                  user.serviceAvailabilityStatus || "available"
                }`}
              >
                {getServiceAvailabilityLabel(user.serviceAvailabilityStatus)}
              </span>
            )}

            <h1>{user.name || "Unnamed User"}</h1>

            {user.intro ? (
              <p className="hero-intro">{user.intro}</p>
            ) : (
              <p className="hero-intro">
                Built for farmers, operators, and real local work.
              </p>
            )}

            {(user.seasonalAvailabilityNote || user.availableStarting) && (
              <div className="availability-detail-strip profile-availability-strip">
                {user.availableStarting && (
                  <span>
                    Starting {new Date(`${user.availableStarting}T00:00:00`).toLocaleDateString()}
                  </span>
                )}
                {user.seasonalAvailabilityNote && (
                  <span>{user.seasonalAvailabilityNote}</span>
                )}
              </div>
            )}

            <div className="profile-quick-stats">
  <span>
  <i>✅</i>
  <strong>{completedWorkCount}</strong>
  <small>Completed jobs</small>
</span>

  <span>
  <i>📅</i>
  <strong>{memberSince}</strong>
  <small>Member since</small>
</span>

  {user.founder === true && user.founderSpotActive !== false && (
    <span>
  <i>🌾</i>
  <strong>Founder</strong>
  <small>Founding Member</small>
</span>
  )}
</div>

   <div className="hero-actions profile-hero-actions">
  {!isOwnProfile && (
    <>
      <button className="profile-message-btn" onClick={handleMessage}>
        💬 Message
      </button>

      <ReportButton
        type="user"
        targetId={userId}
        targetOwnerId={userId}
        targetTitle={user.name || user.displayName || "User profile"}
        buttonLabel="Report User"
        compact
      />
    </>
  )}         

  




              {isOwnProfile && (
                <>
                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() => navigate("/edit-profile")}
                  >
                    ✏️ Edit Profile
                  </button>

                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() =>
                      navigate("/home", { state: { dashboardView: "owner" } })
                    }
                  >
                    My Jobs
                  </button>

                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() =>
                      navigate("/home", { state: { dashboardView: "market" } })
                    }
                  >
                    Market
                  </button>

                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() => navigate("/add-product")}
                  >
                    ➕ Add Product
                  </button>

                  <button
                    className="btn-soft"
                    type="button"
                      onClick={() => navigate("/add-farm-stand")}
                  >
                   🥕 Add Stand / Pickup Spot
                  </button>

                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() => navigate("/add-event")}
                  >
                    ➕ Add Event
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isOwnProfile && (
        <InstallAppButton
          variant="card"
          compact
          source="profile"
          title="Open MyFarmingPal like an app"
          description="Add it to your phone for one-tap access to your profile, stands, jobs, and messages."
        />
      )}

      <div className="profile-content-grid">
        <div className="profile-main-column">
          <div className="profile-card">
            <h3>About</h3>
            {user.bio ? (
              <p>{user.bio}</p>
            ) : (
              <p className="profile-review-empty">
                No bio yet. This user has not added profile details.
              </p>
            )}
          </div>

          {isOwnProfile && profileStorefrontEnabled && (
            <div className="profile-card storefront-readiness-card">
              <div className="storefront-readiness-header">
                <div>
                  <p className="storefront-eyebrow">Public storefront</p>
                  <h3>Storefront Readiness</h3>
                </div>

                {profileStorefrontBadges.length > 0 && (
                  <div className="storefront-badge-row">
                    {profileStorefrontBadges.map((badge) => (
                      <span key={badge.key} className="storefront-profile-badge">
                        {badge.icon ? `${badge.icon} ` : ""}
                        {badge.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {storefrontReadiness.ready ? (
                <p className="storefront-ready-message">
                  Your public storefront is ready to share.
                </p>
              ) : (
                <div className="storefront-checklist" aria-label="Storefront readiness checklist">
                  {storefrontReadiness.items.map((item) => (
                    <div
                      key={item.key}
                      className={`storefront-check-item ${
                        item.complete ? "is-complete" : "is-missing"
                      }`}
                    >
                      <span aria-hidden="true">{item.complete ? "✓" : "•"}</span>
                      <strong>{item.label}</strong>
                      <small>{item.complete ? "Complete" : "Missing"}</small>
                    </div>
                  ))}
                </div>
              )}

              <p className="storefront-privacy-note">
                Privacy first: only your profile photo, public name, bio/about,
                broad location, farm work photos, and active product listings
                appear on your public storefront.
              </p>

              <div className="storefront-share-summary">
                <span>Public name: {publicStorefrontName}</span>
                {publicStorefrontLocation && (
                  <span>Broad location: {publicStorefrontLocation}</span>
                )}
              </div>

              {!hasPermanentStorefrontSlug && (
                <p className="storefront-temporary-note">
                  This link currently uses your public name. Permanent storefront
                  links are coming soon.
                </p>
              )}

              <div className="storefront-share-actions">
                <button
                  type="button"
                  className="btn-soft"
                  disabled={!publicStorefrontUrl}
                  onClick={() => {
                    if (publicStorefrontUrl) {
                      window.open(publicStorefrontUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  View Public Storefront
                </button>

                <button
                  type="button"
                  className="btn-soft"
                  disabled={!publicStorefrontUrl}
                  onClick={handleCopyStorefrontLink}
                >
                  Copy Storefront Link
                </button>
              </div>

              {storefrontCopyStatus && (
                <p className="storefront-copy-status" role="status">
                  {storefrontCopyStatus}
                </p>
              )}
            </div>
          )}

          {featuredImage && (
            <div className="profile-card">
              <div className="profile-card-header-row">
                <h3>Featured Photo</h3>
                <div className="profile-card-note">Click to view larger</div>
              </div>

              <img
                src={featuredImage.url}
                className="featured-img"
                alt="Featured work"
                onClick={() => setSelectedImage(featuredImage.url)}
              />

              {featuredImage.caption && (
                <p className="featured-caption">{featuredImage.caption}</p>
              )}
            </div>
          )}

          {user.workImages?.length > 1 && (
            <div className="profile-card">
              <h3>Gallery</h3>

              <div className="gallery">
                {user.workImages.map((img, index) => (
                  <img
                    key={img.url || index}
                    src={img.url}
                    className="gallery-img"
                    alt={`Gallery ${index + 1}`}
                    onClick={() => setSelectedImage(img.url)}
                  />
                ))}
              </div>
            </div>
          )}
          {canShowStorefrontContent && (
               <div className="profile-card" id="profile-farm-stands">
  <div className="profile-products-header">
    <h3>🥕 Farm Stands / Pickup Spots</h3>

    {isOwnProfile && (
      <button
        className="btn-soft"
        type="button"
        onClick={() => navigate("/add-farm-stand")}
      >
        ➕ Add Stand / Pickup Spot
      </button>
    )}
  </div>



  {farmStands.length === 0 ? (
    <div className="profile-products-empty">
      <strong>No stand / pickup spot listed yet.</strong>
      <p>
        Add a roadside stand, porch pickup, cooler pickup, booth, or local
        pickup spot when you are ready to share fresh items.
      </p>
      {isOwnProfile && (
        <button type="button" className="btn-primary" onClick={() => navigate("/add-farm-stand")}>
          Add Stand / Pickup Spot
        </button>
      )}
    </div>
  ) : (
    <div className="farm-stand-profile-grid">
      {farmStands.map((stand) => {
        const locationLabel = getStandPublicLocationLabel(stand);
        const directionsUrl = getStandDirectionsUrl(stand);
        const standAvailabilityStatus = stand.standAvailabilityStatus || "open_now";

        return (
          <div key={stand.id} className="farm-stand-profile-card">
            <div className="farm-stand-profile-top">
              <div>
                <h4>{stand.standName || "Stand / Pickup Spot"}</h4>
                <p>📍 {locationLabel}</p>
              </div>

              <span
                className={`farm-stand-status-pill availability-${standAvailabilityStatus}`}
              >
                {stand.isActive === false
                  ? "Inactive"
                  : getFarmStandAvailabilityLabel(standAvailabilityStatus)}
              </span>
            </div>

            {(standAvailabilityStatus === "opening_soon" ||
              standAvailabilityStatus === "closed_for_season") && (
              <div className="availability-detail-strip farm-stand-seasonal-strip">
                {standAvailabilityStatus === "opening_soon" && stand.openingDate && (
                  <span>Opening {new Date(`${stand.openingDate}T00:00:00`).toLocaleDateString()}</span>
                )}
                {stand.seasonalNote && <span>{stand.seasonalNote}</span>}
              </div>
            )}

            {stand.description && (
              <p className="farm-stand-profile-desc">
                {stand.description}
              </p>
            )}

            <div className="farm-stand-profile-meta">
              {stand.hoursText && <span>🕒 {stand.hoursText}</span>}

              {Array.isArray(stand.paymentMethods) &&
                stand.paymentMethods.length > 0 && (
                  <span>
                    💵{" "}
                    {stand.paymentMethods
                      .map(
                        (method) =>
                          method.charAt(0).toUpperCase() + method.slice(1)
                      )
                      .join(", ")}
                  </span>
                )}
            </div>

            <div className="profile-card-actions-row">
  {canShowStandDirections(stand) && directionsUrl && (
    <button
      type="button"
      className="btn-soft"
      onClick={() =>
        window.open(
          directionsUrl,
          "_blank"
        )
      }
    >
      📍 Get Directions
    </button>
  )}

  {!canShowStandDirections(stand) && (
    <span className="profile-card-muted-note">Message seller for exact pickup details</span>
  )}

  {isOwnProfile && (
    <>
      <button
        type="button"
        className="btn-soft"
        onClick={() => navigate(`/add-farm-stand?edit=${stand.id}`)}
      >
        ✏️ Edit
      </button>

      <button
        type="button"
        className="profile-remove-product-btn"
        onClick={() => handleArchiveFarmStand(stand.id)}
      >
        Remove
      </button>
    </>
  )}

  {!isOwnProfile && (
    <ReportButton
      type="farmStand"
      targetId={stand.id}
      targetOwnerId={stand.ownerId || userId}
      targetTitle={stand.standName || "Stand / Pickup Spot"}
      buttonLabel="Report"
      compact
    />
  )}
</div>
          </div>
        );
      })}
    </div>
  )}
</div>
          )}

{isOwnProfile && completedBuyerOrdersNeedingReview.length > 0 && (
  <div className="profile-card seller-requests-card">
    <div className="profile-products-header">
      <div>
        <h3>⭐ Product Reviews To Leave</h3>
        <p className="profile-card-note">
          Review products after the seller confirms the sale.
        </p>
      </div>

      <span className="profile-pill">
        {completedBuyerOrdersNeedingReview.length}
      </span>
    </div>

    <div className="seller-request-list">
      {completedBuyerOrdersNeedingReview.map((order) => (
        <div key={order.id} className="seller-request-card">
          {order.productImage && (
            <img
              src={order.productImage}
              alt={order.productName || "Product"}
              className="seller-request-img"
            />
          )}

          <div className="seller-request-body">
            <div className="seller-request-topline">
              <h4>{order.productName || "Farm Product"}</h4>
              <span className="acre-badge">Purchased</span>
            </div>

            <p>
              Bought{" "}
              <strong>
                {Number(order.quantity || 1)} {order.productUnit || "item"}
                {Number(order.quantity || 1) === 1 ? "" : "s"}
              </strong>{" "}
              from {order.sellerName || "Seller"}.
            </p>

            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setProductReviewTarget(order);
                setProductReviewRating(5);
                setProductReviewComment("");
              }}
            >
              ⭐ Review Product
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

{isOwnProfile && (
  <div className="profile-card seller-requests-card" id="product-requests">
    <div className="profile-products-header">
      <div>
        <h3>🛒 Product Requests</h3>
        <p className="profile-card-note">
          Confirm sales after you and the buyer agree. Stock updates only after confirmation.
        </p>
      </div>

      {pendingProductOrders.length > 0 && (
  <span className="profile-pill">
    Pending: {pendingProductOrders.length}
  </span>
)}
    </div>

    {productOrders.length === 0 ? (
      <div className="profile-products-empty">
        No product requests yet.
      </div>
    ) : (
      <div className="seller-request-list">
        {pendingProductOrders.length > 0 && (
          
          <>
            <h4 className="seller-request-group-title">Pending Requests</h4>

            {pendingProductOrders.map((order) => (
              <div key={order.id} className="seller-request-card">
                {order.productImage && (
                  <img
                    src={order.productImage}
                    alt={order.productName || "Product"}
                    className="seller-request-img"
                  />
                )}

                <div className="seller-request-body">
                  <div className="seller-request-topline">
                    <h4>{order.productName || "Farm Product"}</h4>
                    <span className="acre-badge">Pending</span>
                  </div>

                  <p>
                    <strong>{order.buyerName || "Buyer"}</strong> wants{" "}
                    <strong>
                      {Number(order.quantity || 1)} {order.productUnit || "item"}
                      {Number(order.quantity || 1) === 1 ? "" : "s"}
                    </strong>
                    .
                  </p>

                  <p>
                    Price listed:{" "}
                    <strong>
                      ${Number(order.productPrice || 0).toFixed(2)}
                      {order.productUnit ? ` / ${order.productUnit}` : ""}
                    </strong>
                  </p>

                  {order.message && (
                    <p className="seller-request-note">{order.message}</p>
                  )}

                  <div className="seller-request-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => confirmProductSale(order)}
                    >
                      Confirm Sale
                    </button>

                    <button
                      type="button"
                      className="btn-danger-soft"
                      onClick={() => declineProductSale(order)}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {completedProductOrders.length > 0 && (
          <>
            <h4 className="seller-request-group-title">Completed Sales</h4>

            {completedProductOrders.slice(0, 5).map((order) => (
              <div key={order.id} className="seller-request-card is-completed">
                {order.productImage && (
                  <img
                    src={order.productImage}
                    alt={order.productName || "Product"}
                    className="seller-request-img"
                  />
                )}

                <div className="seller-request-body">
                  <div className="seller-request-topline">
                    <h4>{order.productName || "Farm Product"}</h4>
                    <span className="acre-badge">Completed</span>
                  </div>

                  <p>
                    Sold{" "}
                    <strong>
                      {Number(order.quantity || 1)} {order.productUnit || "item"}
                      {Number(order.quantity || 1) === 1 ? "" : "s"}
                    </strong>{" "}
                    to {order.buyerName || "Buyer"}.
                  </p>
                </div>
              </div>
            ))}
          </>
        )}

        {pendingProductOrders.length === 0 &&
          completedProductOrders.length === 0 &&
          declinedProductOrders.length > 0 && (
            <div className="profile-products-empty">
              No active requests right now.
            </div>
          )}
      </div>
    )}
  </div>
)}

          {canShowStorefrontContent && (
          <div className="profile-card" id="profile-products">
            <div className="profile-products-header">
              <h3>🛍 Products</h3>

              
            </div>

            {products.length === 0 ? (
              <div className="profile-products-empty">
                <strong>No products listed yet.</strong>
                <p>
                  Add your first product, plant, handmade item, or farm good so
                  local buyers can find it.
                </p>
                {isOwnProfile && (
                  <button type="button" className="btn-primary" onClick={() => navigate("/add-product")}>
                    Add Product
                  </button>
                )}
              </div>
            ) : (
              <div className="product-grid">
               {products.map((product) => {
                 const availabilityStatus = getProductAvailabilityStatus(product);
                 const isUnavailable =
                   availabilityStatus === PRODUCT_AVAILABILITY.OUT_OF_STOCK ||
                   availabilityStatus === PRODUCT_AVAILABILITY.ARCHIVED;
                 const canManageProduct = currentUserOwnsProduct(product);

                 return (

                
  <div key={product.id} className="product-card profile-product-card">
    <div className="profile-product-image-wrap">
      {product.image ? (
        <img
          src={product.image}
          className="product-img profile-product-image"
          alt={product.name || "Product"}
        />
      ) : (
        <div className="profile-product-image-placeholder">🌾</div>
      )}

      <span
        className={`profile-product-status ${
          isUnavailable ? "is-out" : "is-active"
        }`}
      >
        {getProductAvailabilityLabel(availabilityStatus)}
      </span>
    </div>

    <div className="product-card-body profile-product-body">
      <div className="profile-product-topline">
        <h4>{product.name || "Farm Product"}</h4>

        <span className="profile-product-price">
          <p className="price">
          {formatProductPrice(product)}
        </p>
        </span>
      </div>

{product.productType === "hay_straw" && product.hayDetails && (
  <div className="hay-market-details">
    <span>
      🌾 {product.hayDetails.hayType?.replace("_", " ") || "Hay"}
    </span>

    <span>
      {product.hayDetails.baleType?.replace("_", " ") || "Bale"}
    </span>

    {product.hayDetails.cutting && (
      <span>{product.hayDetails.cutting}</span>
    )}

    {product.hayDetails.baleWeight && (
      <span>{product.hayDetails.baleWeight}</span>
    )}
  </div>
)}

      {product.description && (
        <p className="desc profile-product-description">
          {product.description}
        </p>
      )}

      <div className="profile-product-meta">
        <span>
          Available:{" "}
          <strong>
            {formatAvailability(
              product.quantityAvailable ?? product.quantity ?? 1,
              product.unit
            )}
          </strong>
        </span>
      </div>
      {getReviewsForProduct(product.id).length > 0 && (
  <div className="product-review-inline-list">
    <strong>⭐ Product Reviews</strong>

    {getReviewsForProduct(product.id).slice(0, 3).map((review) => (
      <div key={review.id} className="product-review-inline">
        <div>
          <strong>⭐ {review.rating}</strong>{" "}
          <span>by {review.buyerName || "Buyer"}</span>
        </div>

        <p>{review.comment}</p>
      </div>
    ))}
  </div>
)}
    </div>

    <div className="product-card-actions">
  <button
    className="btn-soft"
    type="button"
    onClick={() => viewProductListing(product)}
  >
    View Product
  </button>

  {canManageProduct && (
    <button
      type="button"
      className="btn-soft"
      onClick={() => editProductListing(product)}
      aria-label={`Edit Product ${product.name || "listing"}`}
    >
      Edit Product
    </button>
  )}

  {!isOwnProfile && (
    <ReportButton
      type="product"
      targetId={product.id}
      targetOwnerId={product.userId || product.ownerId || userId}
      targetTitle={product.name || "Farm Product"}
      buttonLabel="Report"
      compact
    />
  )}

  {canManageProduct && (
    <button
      type="button"
      className="profile-remove-product-btn"
      onClick={() => handleArchiveProduct(product.id)}
    >
      Remove Product
    </button>
  )}
</div>
          )}
  </div>
)})} 
              </div>
            )}
          </div>
          )}

          

          
        </div>

        <aside className="profile-side-column">
          <div className="profile-card">
            <h3>✅ Completed Work</h3>

            {allCompletedWork.length === 0 ? (
              <div className="empty-state">No completed jobs yet.</div>
            ) : (
              <div className="completed-work-list">
                {allCompletedWork.map((job) => {
                  const isDayJob = job.jobKind === "day";

                  return (
                    <div key={`${job.jobKind}-${job.id}`} className="completed-work-card">
                      <h4>
                        {isDayJob
                          ? `🛠 ${job.title || "Day Job"}`
                          : "🌾 Field Job"}
                      </h4>

                      <div className="completed-work-meta">
                        {isDayJob ? (
                          <>
                            <div>
                              <strong>Location:</strong> {getDayJobLocation(job)}
                            </div>

                            {job.category && (
                              <div>
                                <strong>Category:</strong> {job.category}
                              </div>
                            )}

                            {job.payAmount && (
                              <div>
                                <strong>Pay:</strong> ${job.payAmount}
                              </div>
                            )}

                            {job.duration && (
                              <div>
                                <strong>Duration:</strong> {job.duration}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div>
                              <strong>Location:</strong> {getFieldJobLocation(job)}
                            </div>

                            <div>
                              <strong>Acres:</strong> {getFieldTotalAcres(job)}
                            </div>

                            <div>
                              <strong>Crop:</strong> {getFieldCrop(job)}
                            </div>

                            <div>
  <strong>Bid Type:</strong> Bid per acre
</div>
                          </>
                        )}

                        <div>
                          <strong>Completed:</strong> {formatDate(job.completedAt)}
                        </div>
                      </div>

                      <div className="completed-work-badges">
                        <span className="completed-work-badge">
                          ✅ Completed
                        </span>

                        {job.completedByName && (
                          <span className="completed-work-badge">
                            👷 {job.completedByName}
                          </span>
                        )}
                      </div>

                      {getJobReviews(job).length > 0 ? (
  <div className="job-review-list-inline">
    <h4 className="job-review-list-title">⭐ Reviews for this job</h4>

    {getJobReviews(job).map((review) => (
      <div key={review.id} className="job-review-inline">
        <div className="job-review-inline-top">
          <strong>⭐ {review.rating}</strong>
          <span>
            Reviewed by {review.reviewerName || "User"}
            {review.reviewerRole ? ` • ${review.reviewerRole}` : ""}
          </span>
        </div>

        <p className="job-review-inline-comment">
          {review.comment}
        </p>

        {(review.jobImage || review.completionImage || review.reviewImage) && (
          <div className="review-image-strip">
            {review.jobImage && (
              <img
                src={review.jobImage}
                alt="Job"
                onClick={() => setSelectedImage(review.jobImage)}
              />
            )}

            {review.completionImage && (
              <img
                src={review.completionImage}
                alt="Completed work"
                onClick={() => setSelectedImage(review.completionImage)}
              />
            )}

            {review.reviewImage && (
              <img
                src={review.reviewImage}
                alt="Review"
                onClick={() => setSelectedImage(review.reviewImage)}
              />
            )}
          </div>
        )}
      </div>
    ))}
  </div>
) : (
  <div className="job-review-empty">
    No review left for this job yet.
  </div>
)}

                      {job.jobPhotos?.length > 0 && (
                        <div className="completed-work-photos">
                          <strong>Before</strong>

                          {job.jobPhotos.map((img) => (
                            <img key={img} src={img} alt="Before job" />
                          ))}
                        </div>
                      )}

                      {job.completionPhotos?.length > 0 && (
                        <div className="completed-work-photos">
                          <strong>After</strong>

                          {job.completionPhotos.map((img) => (
                            <img key={img} src={img} alt="Completed job" />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>
      </div>
{productReviewTarget && (
  <div className="modal" onClick={() => setProductReviewTarget(null)}>
    <div
      className="product-detail-modal review-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2>⭐ Review Product</h2>

      <p>
        <strong>{productReviewTarget.productName || "Farm Product"}</strong>
      </p>

      <p className="profile-card-note">
        Sold by {productReviewTarget.sellerName || "Seller"}
      </p>

      <label className="form-label">Rating</label>
      <select
        value={productReviewRating}
        onChange={(e) => setProductReviewRating(Number(e.target.value))}
      >
        <option value={5}>⭐⭐⭐⭐⭐ 5 - Excellent</option>
        <option value={4}>⭐⭐⭐⭐ 4 - Good</option>
        <option value={3}>⭐⭐⭐ 3 - Okay</option>
        <option value={2}>⭐⭐ 2 - Poor</option>
        <option value={1}>⭐ 1 - Bad</option>
      </select>

      <label className="form-label">Review</label>
      <textarea
        value={productReviewComment}
        onChange={(e) => setProductReviewComment(e.target.value)}
        rows={4}
        placeholder="Example: Fresh, exactly as described, easy pickup."
      />

      <div className="button-group">
        <button
          type="button"
          className="btn-soft"
          onClick={() => setProductReviewTarget(null)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          disabled={submittingProductReview}
          onClick={submitProductReview}
        >
          {submittingProductReview ? "Submitting..." : "Submit Review"}
        </button>
      </div>
    </div>
  </div>
)}

      {selectedImage && (
        <div className="modal" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} className="modal-img" alt="preview" />
        </div>
      )}

      
    </div>
  );
};

export default Profile;
